/**
 * @element Badge
 * @description The status pill, used at ~100 call sites.
 *
 * ⚠️ TWO DEFECTS FIXED HERE, BOTH OF THEM PRIMITIVE-LEVEL.
 *
 * 1. IT DROPPED ITS OWN CONTENT.
 *    The signature was `({ label, status })` — no `children`, no `className`,
 *    no `tone`. 49 call sites pass children (`<Badge>{c.stage}</Badge>`) and
 *    3 pass `tone=`; every one of them rendered an EMPTY pill. MyLadder had
 *    already found this and fixed its own three call sites by rewriting them
 *    to `label=`; the other 49 were never found, because the primitive fails
 *    silently — an empty pill looks like a deliberate spacer, not a bug.
 *    Fixing the three call sites was fixing the incident. `children` and
 *    `tone` are accepted here, so the remaining 49 light up and the next one
 *    written either way works.
 *
 * 2. THE TYPE WAS SET TO BE UNREADABLE.
 *    `text-[9px] font-black uppercase tracking-tighter` — three things
 *    fighting the reader at once, and they compound rather than trade off:
 *    9px is below the size at which uppercase letterforms stay distinct;
 *    `font-black` at 9px fills the counters so glyphs read as blocks; and
 *    `tracking-tighter` is NEGATIVE letter-spacing, when uppercase — set from
 *    capitals, with no ascender/descender rhythm to separate them — needs
 *    POSITIVE tracking. On /ladder that rendered "PAID UNLOCKED · 55%" as a
 *    smear, at the moment the page was telling a reviewer their pay rate.
 *
 * Renders a <span>: a badge annotates a line of content and is routinely
 * placed inside <p>/<label>/<td>, where the old <div> was invalid nesting.
 *
 * @param {string} label     Pill text. `children` is the equivalent.
 * @param {string} status    neutral | success | warning | danger | info
 * @param {string} tone      Alias for `status` ('default' → neutral).
 * @param {node}   icon      Optional leading glyph.
 * @param {string} className Extra classes. Supplying your own `bg-`/`text-`
 *                           colour suppresses the status palette, so the two
 *                           cannot both emit a background and race.
 */
import { mergeClasses } from '../../utils/classes.js';

const PALETTE = {
  neutral: 'bg-gray-100 text-gray-600 border-gray-200',
  success: 'bg-green-50 text-green-700 border-green-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  danger:  'bg-red-50 text-red-700 border-red-200',
  info:    'bg-blue-50 text-blue-700 border-blue-200',
};

const TONE_ALIASES = { default: 'neutral', error: 'danger', warn: 'warning' };

export const Badge = ({
  label,
  children,
  status,
  tone,
  icon = null,
  className = '',
}) => {
  const key = status || TONE_ALIASES[tone] || tone || 'neutral';

  // A caller that brings its own colour owns the colour. Concatenating both
  // emits two equal-specificity `bg-*` rules and lets STYLESHEET order pick
  // the winner — see utils/classes.js, which is where that rule now lives so
  // Badge and Text resolve it the same way rather than each inventing one.
  return (
    <span
      className={mergeClasses(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-transparent',
        'px-2 py-0.5 text-[10px] font-semibold uppercase leading-4 tracking-[0.06em]',
        PALETTE[key] || PALETTE.neutral,
        className
      )}
    >
      {icon}
      {label ?? children}
    </span>
  );
};

export default Badge;
